import { RECONNECT_MAX_RETRIES } from '../constants';
import type { VoiceLiveContext, VoiceLiveEvent } from './types';

/**
 * Pure action logic for the Voice Live machine, extracted from
 * `voiceLiveMachine.ts` so the machine file reads as the state graph.
 *
 * These are plain functions: the assigners return a `Partial<VoiceLiveContext>`
 * patch and the machine wires each through `assign()` in `setup()`, which is
 * where XState supplies the full machine types (notably the event union). The
 * effect stubs are no-ops; `useVoiceLiveSession` replaces them with real
 * implementations via `.provide()` at the React boundary.
 */

/** Superset of the args XState passes to an action; extra members are ignored. */
type ActionArgs = { context: VoiceLiveContext; event: VoiceLiveEvent };

// ── wsSession region ────────────────────────────────────────────────────────

/** Fresh user-initiated start: clear retry/error and permit reconnects. */
export const resetForStart = ({ context }: ActionArgs): Partial<VoiceLiveContext> => ({
  retryCount: 0,
  shouldReconnect: true,
  errorMessage: '',
  degradedToAudio: false,
  savedUserId: context.userId,
});

/** Manual retry after a failure: same reset, reusing the saved user/session. */
export const prepareManualRetry = ({ context }: ActionArgs): Partial<VoiceLiveContext> => ({
  retryCount: 0,
  shouldReconnect: true,
  errorMessage: '',
  savedUserId: context.userId,
});

/**
 * Per-attempt reset when (re)entering `connecting`. Re-deriving `socketIsAudioOnly` here
 * keeps audio routing matched to how the socket is actually built, and runs before any
 * server audio frames arrive — which keeps a post-degrade / post-retry audio session from
 * being silently gated off.
 */
export const prepareConnectingAttempt = ({ context }: ActionArgs): Partial<VoiceLiveContext> => ({
  sessionReady: false,
  hasIceServers: false,
  iceServers: [],
  socketIsAudioOnly: !context.isVideoEnabled,
});

/** Successful socket open: clear the reconnect counter and any error. */
export const onWsOpen = (): Partial<VoiceLiveContext> => ({
  retryCount: 0,
  errorMessage: '',
});

/** session.updated received — the session is live. */
export const onSessionReady = (): Partial<VoiceLiveContext> => ({ sessionReady: true });

/** Terminal disconnect: choose the user-facing error for the close reason. */
export const onFinalDisconnect = (
  { context }: ActionArgs,
  { code }: { code: number },
): Partial<VoiceLiveContext> => {
  let errorMessage = 'Connection closed. Please try again.';
  if (context.retryCount >= RECONNECT_MAX_RETRIES) {
    errorMessage = 'Unable to maintain connection. Please check your internet and try again.';
  } else if (code === 4001) {
    errorMessage = 'Authentication failed. Please refresh and try again.';
  } else if (!context.shouldReconnect) {
    // Intentional STOP clears the error before the socket closes. A terminal
    // subsystem failure disables reconnect while retaining its own message.
    errorMessage = context.errorMessage;
  }
  return { sessionReady: false, errorMessage };
};

/** Terminal microphone failure: stop reconnecting and preserve its UI message. */
export const onMicCaptureFailed = (
  _: ActionArgs,
  { message }: { message: string },
): Partial<VoiceLiveContext> => ({
  shouldReconnect: false,
  retryCount: 0,
  errorMessage: message,
  sessionReady: false,
  hasIceServers: false,
  iceServers: [],
});

/** One more reconnect attempt is being made. */
export const incrementRetry = ({ context }: ActionArgs): Partial<VoiceLiveContext> => ({
  retryCount: context.retryCount + 1,
});

/** Store ICE servers delivered by the backend (ignore empty deliveries). */
export const storeIceServers = (
  _: ActionArgs,
  { servers }: { servers: RTCIceServer[] },
): Partial<VoiceLiveContext> =>
  servers.length === 0 ? {} : { iceServers: servers, hasIceServers: true };

/** Persist the FieldOne session id once the backend creates it. */
export const storeFieldOneSession = (
  _: ActionArgs,
  { sessionId }: { sessionId: number },
): Partial<VoiceLiveContext> => ({ fieldOneSessionId: sessionId });

/** Set the user-facing error message (raised by the peer/audio subsystems). */
export const setError = (
  _: ActionArgs,
  { message }: { message: string },
): Partial<VoiceLiveContext> => ({ errorMessage: message });

/** Update the video (avatar) vs audio-only preference. */
export const setVideoEnabled = (
  _: ActionArgs,
  { enabled }: { enabled: boolean },
): Partial<VoiceLiveContext> => ({ isVideoEnabled: enabled });

/** Reset connection state on a user-initiated stop. */
export const resetConnectionOnStop = (): Partial<VoiceLiveContext> => ({
  shouldReconnect: false,
  retryCount: 0,
  errorMessage: '',
  sessionReady: false,
  hasIceServers: false,
  iceServers: [],
});

// ── Effects (no-op stubs; provided by useVoiceLiveSession via .provide()) ────

export const createWebSocket = () => {};
export const teardown = () => {};
export const forceCloseSocket = () => {};
export const handleIceServerTimeout = () => {};
/** Auto-start the WebRTC avatar once session + ICE are ready (video mode). */
export const maybeAutoStartAvatar = () => {};

// ── avatar region ───────────────────────────────────────────────────────────

/** Avatar connected: stop counting failures and remember it came up. */
export const markAvatarActive = (): Partial<VoiceLiveContext> => ({
  avatarEverActive: true,
  initialAvatarFailures: 0,
});

/** Count one INITIAL avatar establishment failure. */
export const countAvatarFailure = ({ context }: ActionArgs): Partial<VoiceLiveContext> => ({
  initialAvatarFailures: context.initialAvatarFailures + 1,
});

/** Avatar dropped after a successful connect with no reconnect pending. */
export const setAvatarDropError = (): Partial<VoiceLiveContext> => ({
  errorMessage: 'Avatar connection failed. Please try restarting.',
});

/** Degrade to audio-only: flip the flags (the reconnect is raised separately). */
export const enterAudioFallback = (): Partial<VoiceLiveContext> => ({
  socketIsAudioOnly: true,
  degradedToAudio: true,
  isVideoEnabled: false,
  errorMessage: '',
});

/** "Try video again" from degraded: reset gating for a fresh avatar attempt. */
export const reattemptAvatar = (): Partial<VoiceLiveContext> => ({
  socketIsAudioOnly: false,
  degradedToAudio: false,
  avatarEverActive: false,
  initialAvatarFailures: 0,
  isVideoEnabled: true,
  errorMessage: '',
});

/**
 * Mirror of {@link reattemptAvatar}: "audio only" picked by the user before a peer
 * came up. `socketIsAudioOnly` is set here rather than left to the next `connecting`
 * entry so the server-message router stops discarding audio deltas immediately.
 */
export const enterVoluntaryAudio = (): Partial<VoiceLiveContext> => ({
  socketIsAudioOnly: true,
  degradedToAudio: false,
  avatarEverActive: false,
  initialAvatarFailures: 0,
  isVideoEnabled: false,
  errorMessage: '',
});

/**
 * Reset fallback gating (session start/stop/manual retry). Does NOT touch
 * `socketIsAudioOnly` — that is re-derived from the video preference on the
 * next `connecting` entry, which avoids desyncing it from the socket mode.
 * `degradedToAudio` is likewise preserved so a manual retry keeps its banner.
 */
export const resetFallback = (): Partial<VoiceLiveContext> => ({
  avatarEverActive: false,
  initialAvatarFailures: 0,
});

/** Tear down the failed peer and re-run startAvatar after a short delay. */
export const retryAvatarPeer = () => {};

/** Drop the avatar video element while the peer (and its audio) keeps running. */
export const detachAvatarVideo = () => {};
